import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const sanitizeFilename = filename => {
  if (!filename) return 'export';

  // First normalize the string and replace diacritics with their ASCII equivalents
  // NFD separates base characters from diacritics
  // Then we remove the diacritical marks (Unicode range 0300-036F)
  const withoutDiacritics = filename
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  // Then apply standard filename character sanitization
  return withoutDiacritics
    .replace(/[/\\?%*:|"<>]/g, '-') // Replace invalid chars with dash
    .replace(/\s+/g, '_') // Replace spaces with underscore
    .replace(/&/g, '_and_') // Replace ampersand
    .replace(/[^\w\-_.]/g, '') // Remove any remaining unsafe chars
    .trim(); // Trim whitespace
};

export const exportTableToPdf = async (fileName = 'Material-Table') => {
  try {
    const tableWrap = document.querySelector('[role="table"]') as HTMLElement;
    const tableHeader = tableWrap.querySelector('.header') as HTMLElement;

    if (!tableWrap && !tableHeader) {
      console.error('Table element not found. Please check the DOM structure.');
      return false;
    }

    // Get the table dimensions
    const tableWrapRect = tableWrap.getBoundingClientRect();
    const tableHeaderReact = tableHeader.getBoundingClientRect();

    const tableDimensions = {
      height: tableWrapRect.height,
      width: tableHeaderReact.width,
    };

    if (tableDimensions.width === 0 || tableDimensions.height === 0) {
      console.error(
        'Table has zero width or height. It might be hidden or not fully rendered.'
      );
      return false;
    }

    // Capture the table with adjusted settings
    const canvas = await html2canvas(tableWrap, {
      scale: 2, // Higher scale for better quality
      logging: true,
      useCORS: true,
      allowTaint: true,
      width: tableDimensions.width,
      height: tableDimensions.height,
      windowWidth: tableDimensions.width,
      windowHeight: tableDimensions.height,
    });

    // Create PDF with appropriate size
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'A4',
    });

    // Get PDF dimensions
    const pdfWidth = doc.internal.pageSize.getWidth();
    const pdfHeight = doc.internal.pageSize.getHeight();

    // Calculate scales to fit the entire width
    const imgData = canvas.toDataURL('image/png', 1.0);

    // Scale image to fit PDF width
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = pdfWidth / imgWidth;
    const scaledWidth = imgWidth * ratio;
    const scaledHeight = imgHeight * ratio;

    // Image position
    const x = 0;
    const y = 0;

    // Add image to the first page
    doc.addImage(
      imgData,
      'PNG',
      x,
      y,
      scaledWidth,
      scaledHeight,
      undefined,
      'FAST'
    );

    // If the scaled height exceeds the page height, add more pages
    let remainingHeight = scaledHeight - pdfHeight;
    let offsetY = pdfHeight;

    while (remainingHeight > 0) {
      doc.addPage();
      doc.addImage(
        imgData,
        'PNG',
        x,
        -offsetY,
        scaledWidth,
        scaledHeight,
        undefined,
        'FAST'
      );

      offsetY += pdfHeight;
      remainingHeight -= pdfHeight;
    }

    // Sanitize the file name
    const sanitizedFileName = sanitizeFilename(fileName);

    doc.save(`${sanitizedFileName}.pdf`);

    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return false;
  }
};
